import axios from 'axios';
import { getWebApiBaseUrl } from './config/apiBaseUrl';

export { getWebApiBaseUrl } from './config/apiBaseUrl';

const API_URL = getWebApiBaseUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_URL);
}

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', config.method?.toUpperCase(), config.url, 'with token');
    }
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.warn('API Request without token:', config.method?.toUpperCase(), config.url);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle token expiration and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle 401 (Unauthorized) or 403 (Forbidden) - token expired or invalid
      if (error.response.status === 401 || error.response.status === 403) {
        const errorMessage = error.response.data?.error || 'Invalid or expired token';
        console.error('Token error:', errorMessage);
        
        // Clear invalid token
        const token = localStorage.getItem('token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Cleared invalid token, was:', token ? token.substring(0, 20) + '...' : 'null');
        }
        
        // Only redirect if we're not already on login/register page
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/register' && currentPath !== '/survey' && currentPath !== '/survay') {
          // Dispatch a custom event that AuthContext can listen to
          window.dispatchEvent(new CustomEvent('auth:token-expired', { detail: { error: errorMessage } }));
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error:', error.message);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  username: string;
  email: string;
  profile_picture?: string;
  background_image?: string;
  bio?: string;
  instagram_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  website_url?: string;
}

export interface State {
  id: number;
  name: string;
  abbreviation: string;
}

export interface City {
  id: number;
  name: string;
  state_id: number;
  state_name?: string;
  state_abbreviation?: string;
}

export interface DeliveryTime {
  min: number;
  max: number;
  unit: string;
  display: string;
}

export interface Listing {
  id: number;
  title: string;
  is_shop_store?: number | boolean;
  shop_store_name?: string;
  shop_store_address?: string;
  /** Address from listing upload (location for this listing), not store address. */
  address?: string;
  shop_store_close_time?: string;
  store_id?: number;
  store_name?: string;
  store_profile_picture?: string | null;
  seller_profile_picture?: string | null;
  store_close_time?: string;
  store_returns_money_back?: boolean;
  seller_hours?: string | Record<string, { open: string; close: string; closed: boolean }> | null;
  seller_open_time?: string | null;
  seller_close_time?: string | null;
  description: string;
  price: number;
  category: string;
  image_url: string | null;
  image_urls?: string[];
  user_id: number;
  city_id: number;
  state_id: number;
  seller_name: string;
  state_name: string;
  state_abbreviation: string;
  city_name: string;
  created_at: string;
  sold?: number | boolean;
  quantity?: number;
  delivery_time_option?: string;
  delivery_time?: DeliveryTime;
  accepts_cash_on_delivery?: number | boolean;
  returns_money_back?: number | boolean;
  discount_type?: 'percentage' | 'fixed' | null;
  discount_value?: number | null;
  discount_min_quantity?: number | null;
  discount_code?: string | null;
  discount_code_type?: 'percentage' | 'fixed' | null;
  discount_code_value?: number | null;
  shipping_fee?: number | null;
  max_delivery_miles?: number | null;
  delivery_fee_tiers?: string | null;
  condition?: 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts';
  seller_badge?: {
    positive_percentage: number;
    total_ratings: number;
    formatted_count: string;
    is_top_rated_plus: boolean;
    has_fast_shipping?: boolean;
    has_tracking?: boolean;
    returns_money_back?: boolean;
  };
  store_badge?: {
    positive_percentage: number;
    total_ratings: number;
    formatted_count: string;
    is_top_rated_plus: boolean;
    has_fast_shipping?: boolean;
    has_tracking?: boolean;
    returns_money_back?: boolean;
  };
  item_rating?: {
    average_rating: number;
    total_ratings: number;
    accurate_description?: number;
    shipping_speed?: number;
    communication?: number;
    would_recommend_percentage?: number;
    good_quality_percentage?: number;
    value_for_price_percentage?: number;
  };
  item_reviews?: Array<{
    id: number;
    rating: number;
    buyer_name: string;
    comment?: string;
    created_at: string;
    item_quality?: number;
    shipping?: number;
    customer_service?: number;
    would_recommend?: boolean;
  }>;
  nearby_cities?: {
    primary: string;
    nearby: string[];
  } | null;
}

// Auth API
export const authAPI = {
  register: async (username: string, email: string, password: string) => {
    const response = await api.post('/auth/register', { username, email, password });
    return response.data;
  },
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  forgotPassword: async (email: string, captchaToken?: string) => {
    const response = await api.post('/auth/forgot-password', {
      email,
      ...(captchaToken ? { captchaToken } : {}),
    });
    return response.data;
  },
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
  googleLogin: async (credential: string) => {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },
  facebookLogin: async (accessToken: string) => {
    const response = await api.post('/auth/facebook', { accessToken });
    return response.data;
  },
  updateUsername: async (username: string) => {
    const response = await api.put('/auth/username', { username });
    return response.data;
  },
  updateProfilePicture: async (profile_picture: string) => {
    const response = await api.put('/auth/profile-picture', { profile_picture });
    return response.data;
  },
  updateBackgroundImage: async (background_image: string) => {
    const response = await api.put('/auth/background-image', { background_image });
    return response.data;
  },
  getPayoutPreference: async () => {
    const response = await api.get('/auth/payout-preference');
    return response.data;
  },
  updatePayoutPreference: async (prefers_instant_payout: boolean) => {
    const response = await api.put('/auth/payout-preference', { prefers_instant_payout });
    return response.data;
  },
  updateEmail: async (email: string) => {
    const response = await api.put('/auth/email', { email });
    return response.data;
  },
  deactivateAccount: async (password: string) => {
    const response = await api.post('/auth/deactivate', { password });
    return response.data;
  },
  updateBio: async (bio: string) => {
    const response = await api.put('/auth/bio', { bio });
    return response.data;
  },
  updateSellerHours: async (seller_hours: Record<string, { open: string; close: string; closed: boolean }>) => {
    const response = await api.put('/auth/seller-hours', { seller_hours });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  updateSocialLinks: async (links: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
  }) => {
    const response = await api.put('/auth/social-links', links);
    return response.data;
  },
};

// Listings API
export const listingsAPI = {
  getAll: async (filters?: { state_id?: number; city_id?: number; category?: string; search?: string; item_type?: string; light?: number; limit?: number; offset?: number }) => {
    const response = await api.get('/listings', { params: filters });
    return response.data;
  },
  getMine: async (opts?: { limit?: number; offset?: number }) => {
    const params: Record<string, number> = {};
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    const response = await api.get('/listings/mine', { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },
  getOne: async (id: number) => {
    const response = await api.get(`/listings/${id}`);
    return response.data;
  },
  recordView: async (id: number, viewerDeviceId: string) => {
    const deviceId = viewerDeviceId?.trim();
    if (!deviceId) return;
    try {
      await api.post(`/listings/${id}/view`, { viewer_device_id: deviceId });
    } catch {
      /* fire and forget */
    }
  },
  recordCartAdd: async (id: number) => {
    try { await api.post(`/listings/${id}/cart-add`); } catch { /* fire and forget */ }
  },
  getLikeStatus: async (id: number) => {
    const response = await api.get(`/listings/${id}/like-status`);
    return response.data as { liked: boolean };
  },
  toggleLike: async (id: number) => {
    const response = await api.post(`/listings/${id}/like`);
    return response.data as { liked: boolean };
  },
  getRecommended: async (limit?: number, opts?: { city?: string; state?: string }) => {
    const params: Record<string, string | number> = {};
    if (limit) params.limit = limit;
    if (opts?.city) params.city = opts.city;
    if (opts?.state) params.state = opts.state;
    const response = await api.get('/listings/recommended', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data as { items: any[]; limit: number };
  },
  getItemReviews: async (id: number) => {
    const response = await api.get(`/listings/${id}/reviews`);
    return response.data;
  },
  rateItem: async (purchaseId: number, listingId: number, ratingData: {
    rating: number;
    would_recommend?: boolean | null;
    good_quality?: boolean | null;
    value_for_price?: boolean | null;
    comment?: string;
  }) => {
    const response = await api.post(`/listings/${listingId}/rate`, {
      purchase_id: purchaseId,
      ...ratingData
    });
    return response.data;
  },
  create: async (listing: {
    title: string;
    description: string;
    price?: number;
    category?: string;
    image_url?: string;
    image_urls?: string[];
    city_id?: number;
    city_name?: string;
    state_id: number;
    quantity?: number;
    delivery_time_option?: string;
    accepts_cash_on_delivery?: boolean;
    returns_money_back?: boolean;
    store_id?: number;
    is_shop_store?: boolean;
    shop_store_name?: string;
    shop_store_address?: string;
    address?: string;
    shop_store_close_time?: string;
    discount_type?: 'percentage' | 'fixed';
    discount_value?: number;
    discount_min_quantity?: number;
    discount_code?: string | null;
    discount_code_type?: 'percentage' | 'fixed' | null;
    discount_code_value?: number | null;
    shipping_fee?: number | string | null;
    max_delivery_miles?: number | null;
    free_delivery_miles?: number;
    delivery_fee_tiers?: string | null;
    condition?: 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts';
    seller_hours?: Record<string, { open: string; close: string; closed: boolean }>;
    created_platform?: 'ios' | 'android' | 'web';
  }) => {
    const response = await api.post('/listings', listing);
    return response.data;
  },
  update: async (id: number, listing: {
    title?: string;
    description?: string;
    price?: number;
    category?: string;
    image_url?: string;
    image_urls?: string[];
    city_id?: number;
    city_name?: string;
    state_id?: number;
    quantity?: number;
    delivery_time_option?: string;
    accepts_cash_on_delivery?: boolean;
    returns_money_back?: boolean;
    store_id?: number;
    is_shop_store?: boolean;
    shop_store_name?: string;
    shop_store_address?: string;
    address?: string;
    shop_store_close_time?: string;
    discount_type?: 'percentage' | 'fixed';
    discount_value?: number;
    discount_min_quantity?: number;
    discount_code?: string | null;
    discount_code_type?: 'percentage' | 'fixed' | null;
    discount_code_value?: number | null;
    shipping_fee?: number | string | null;
    max_delivery_miles?: number | null;
    free_delivery_miles?: number;
    delivery_fee_tiers?: string | null;
    condition?: 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts';
    seller_hours?: Record<string, { open: string; close: string; closed: boolean }>;
  }) => {
    const response = await api.put(`/listings/${id}`, listing);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/listings/${id}`);
    return response.data ?? { message: 'Deleted' };
  },
  purchase: async (id: number, deliveryAddress: string, addressData?: { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string } | null, deliveryInstructions?: string) => {
    const response = await api.post(`/listings/${id}/purchase`, { deliveryAddress, addressData, deliveryInstructions });
    return response.data;
  },
  getTopCategories: async () => {
    const response = await api.get('/listings/categories/top');
    return response.data;
  },
};

// Locations API
export const locationsAPI = {
  addCity: async (name: string, state_id: number) => {
    const response = await api.post('/locations/cities', { name, state_id });
    return response.data;
  },
  getStates: async () => {
    const response = await api.get('/locations/states');
    return response.data;
  },
  getCitiesByState: async (stateId: number) => {
    const response = await api.get(`/locations/states/${stateId}/cities`);
    return response.data;
  },
  getAllCities: async () => {
    const response = await api.get('/locations/cities');
    return response.data;
  },
  lookupZipCode: async (zipCode: string) => {
    const response = await api.get(`/locations/zip/${zipCode}`);
    return response.data;
  },
  createCity: async (name: string, stateId: number) => {
    const response = await api.post('/locations/cities', { name, state_id: stateId });
    return response.data as { id: number; name: string; state_id: number };
  },
  geocode: async (address: string): Promise<{ lat: number; lon: number } | { lat: null; lon: null }> => {
    const response = await api.get('/locations/geocode', { params: { address } });
    return response.data;
  },
  addressSuggest: async (
    q: string
  ): Promise<{
    suggestions: Array<{
      address: string;
      city: string;
      state: string;
      stateAbbreviation: string;
      postalCode: string;
    }>;
  }> => {
    const response = await api.get('/locations/address-suggest', { params: { q } });
    return response.data;
  },
};

// Purchases API
export const purchasesAPI = {
  getMyOrders: async (opts?: { limit?: number; offset?: number }) => {
    const params: Record<string, number> = {};
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    const response = await api.get('/purchases/my-orders', { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },
  getOrder: async (id: number) => {
    const response = await api.get(`/purchases/${id}`);
    return response.data;
  },
  getMySales: async (opts?: { limit?: number; offset?: number }) => {
    const params: Record<string, number> = {};
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    const response = await api.get('/purchases/my-sales', { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },
  getDashboard: async () => {
    const response = await api.get('/purchases/dashboard');
    return response.data;
  },
  updateStatus: async (
    purchaseId: number,
    status: string,
    deliveryPhoto?: string,
    estimatedArrival?: string,
    buyerMessage?: string
  ) => {
    const payload: Record<string, unknown> = {
      status,
      delivery_photo: deliveryPhoto,
      estimated_arrival: estimatedArrival,
    };
    if (buyerMessage != null && String(buyerMessage).trim()) {
      payload.buyer_message = String(buyerMessage).trim();
    }
    const response = await api.put(`/purchases/${purchaseId}/status`, payload);
    return response.data;
  },
  getTracking: async (orderId: number) => {
    const response = await api.get(`/purchases/${orderId}/tracking`);
    return response.data;
  },
};

// Sellers API
export const sellersAPI = {
  getProfile: async (sellerId: number) => {
    const response = await api.get(`/sellers/${sellerId}`);
    return response.data;
  },
  rateSeller: async (sellerId: number, purchaseId: number, rating: number, comment?: string, accurateDescription?: number, shippingSpeed?: number, communication?: number, overallExperience?: 'positive' | 'neutral' | 'negative') => {
    const response = await api.post(`/sellers/${sellerId}/rate`, {
      purchase_id: purchaseId,
      rating,
      comment,
      accurate_description: accurateDescription,
      shipping_speed: shippingSpeed,
      communication: communication,
      overall_experience: overallExperience,
    });
    return response.data;
  },
  getFeedback: async (sellerId: number) => {
    const response = await api.get(`/sellers/${sellerId}/feedback`);
    return response.data;
  },
  /** Seller-only: defaults + merged templates for in-app buyer auto-replies */
  getBuyerAutoMessages: async () => {
    const response = await api.get('/sellers/me/auto-messages');
    return response.data as {
      defaults: Record<string, string>;
      templates: Record<string, string>;
      placeholders: string[];
      hint?: string;
    };
  },
  updateBuyerAutoMessages: async (templates: Record<string, string>) => {
    const response = await api.put('/sellers/me/auto-messages', templates);
    return response.data as { success: boolean; templates: Record<string, string> };
  },
};

// Payments API
export const paymentsAPI = {
  createPaymentIntent: async (amount: number, listingId?: number, saveCard?: boolean) => {
    const response = await api.post('/payments/create-payment-intent', {
      amount,
      listing_id: listingId,
      save_card: saveCard,
    });
    return response.data;
  },
  confirmPayment: async (
    paymentIntentId: string,
    listingId?: number,
    deliveryAddress?: string,
    addressData?: { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string } | null,
    deliveryInstructions?: string,
    deliveryPreference?: 'leave_at_door' | 'meet_at_door',
    priceBreakdown?: { item_price: number; shipping_fee: number; sales_tax: number; total_amount: number; quantity?: number; listings?: Array<{ listing_id: number; quantity: number }> }
  ) => {
    const body: Record<string, unknown> = {
      paymentIntentId,
      listingId,
      deliveryAddress,
      addressData,
      deliveryInstructions,
      deliveryPreference: deliveryPreference || 'leave_at_door',
    };
    if (priceBreakdown) {
      body.item_price = priceBreakdown.item_price;
      body.shipping_fee = priceBreakdown.shipping_fee;
      body.sales_tax = priceBreakdown.sales_tax;
      body.total_amount = priceBreakdown.total_amount;
      if (priceBreakdown.quantity != null) body.quantity = priceBreakdown.quantity;
      if (priceBreakdown.listings) body.listings = priceBreakdown.listings;
    }
    const response = await api.post('/payments/confirm-payment', body);
    return response.data;
  },
  getPaymentMethods: async () => {
    const response = await api.get('/payments/payment-methods');
    return response.data;
  },
  deletePaymentMethod: async (paymentMethodId: string) => {
    const response = await api.delete(`/payments/payment-methods/${paymentMethodId}`);
    return response.data;
  },
};

// S3 API
export const s3API = {
  getUploadUrl: async (fileName: string, fileType: string) => {
    const response = await api.post('/s3/upload-url', { fileName, fileType });
    return response.data;
  },
  getUploadUrls: async (files: Array<{ fileName: string; fileType: string }>) => {
    const response = await api.post('/s3/upload-urls', { files });
    return response.data;
  },
  uploadProfileImage: async (fileName: string, fileType: string, imageType: 'profile' | 'background') => {
    const response = await api.post('/s3/upload-profile-image', { fileName, fileType, imageType });
    return response.data;
  },
  uploadStoreImage: async (fileName: string, fileType: string, imageType: 'profile' | 'background', storeId: number) => {
    const response = await api.post('/s3/upload-store-image', { fileName, fileType, imageType, storeId });
    return response.data;
  },
  deleteImage: async (fileName: string) => {
    const response = await api.delete('/s3/delete-image', { data: { fileName } });
    return response.data;
  },
};

// Store Rating interface
export interface StoreRating {
  id: number;
  store_id: number;
  buyer_id: number;
  purchase_id: number;
  rating: number;
  comment?: string;
  accurate_description?: number;
  shipping_speed?: number;
  communication?: number;
  created_at: string;
  buyer_name?: string;
}

// Store interface
export interface Store {
  id: number;
  name: string;
  address: string;
  open_time?: string;
  close_time?: string;
  days_open?: string;
  hours?: string | { [key: string]: { open: string; close: string; closed: boolean } };
  owner_id: number;
  owner_name?: string;
  city_id: number;
  state_id: number;
  city_name?: string;
  state_name?: string;
  state_abbreviation?: string;
  returns_money_back?: boolean;
  product_type?: string;
  website?: string | null;
  tags?: string | null;
  profile_picture?: string;
  background_image?: string;
  created_at: string;
  listings?: Listing[];
  ratings?: {
    average: number;
    total: number;
    positive_percentage?: number;
    distribution: {
      5: number;
      4: number;
      3: number;
      2: number;
      1: number;
    };
    reviews: StoreRating[];
    detailed_ratings?: {
      accurate_description: number;
      shipping_speed: number;
      communication: number;
    };
  };
}

// Stores API
export const storesAPI = {
  getAll: async (filters?: { state_id?: number; city_id?: number; search?: string }) => {
    const response = await api.get('/stores', { params: filters });
    return response.data;
  },
  getOne: async (id: number) => {
    const response = await api.get(`/stores/${id}`);
    return response.data;
  },
  getBySlug: async (slug: string) => {
    const response = await api.get(`/stores/${slug}`);
    return response.data;
  },
  create: async (store: {
    name: string;
    address: string;
    open_time?: string;
    close_time?: string;
    days_open?: string;
    hours?: string;
    city_id: number;
    state_id: number;
    returns_money_back?: boolean;
    product_type?: string;
    website?: string;
    tags?: string;
  }) => {
    const response = await api.post('/stores', store);
    return response.data;
  },
  update: async (id: number, store: {
    name?: string;
    address?: string;
    open_time?: string;
    close_time?: string;
    days_open?: string;
    hours?: string;
    returns_money_back?: boolean;
    product_type?: string;
    website?: string;
    tags?: string;
  }) => {
    const response = await api.put(`/stores/${id}`, store);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/stores/${id}`);
    return response.data;
  },
  getMyStores: async () => {
    const response = await api.get('/stores/my/stores');
    return response.data;
  },
  updateProfilePicture: async (storeId: number, profile_picture: string) => {
    const response = await api.put(`/stores/${storeId}/profile-picture`, { profile_picture });
    return response.data;
  },
  updateBackgroundImage: async (storeId: number, background_image: string) => {
    const response = await api.put(`/stores/${storeId}/background-image`, { background_image });
    return response.data;
  },
  rateStore: async (storeId: number, purchaseId: number, rating: number, comment?: string, accurateDescription?: number, shippingSpeed?: number, communication?: number, overallExperience?: 'positive' | 'neutral' | 'negative') => {
    const response = await api.post(`/stores/${storeId}/rate`, {
      purchase_id: purchaseId,
      rating,
      comment,
      accurate_description: accurateDescription,
      shipping_speed: shippingSpeed,
      communication: communication,
      overall_experience: overallExperience,
    });
    return response.data;
  },
  getFeedback: async (storeId: number) => {
    const response = await api.get(`/stores/${storeId}/feedback`);
    return response.data;
  },
};

// Message interfaces
export interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  listing_id?: number;
  message: string;
  read: number;
  created_at: string;
  sender_name?: string;
  receiver_name?: string;
  listing_title?: string;
}

export interface Conversation {
  other_user_id: number;
  other_user_name: string;
  listing_id?: number;
  listing_title?: string;
  listing_image?: string;
  last_message?: string;
  last_message_time?: string;
  unread_count: number;
}

// Messages API
// Stripe Connect API
export const stripeConnectAPI = {
  createAccount: async () => {
    const response = await api.post('/stripe-connect/create-account', { source: 'web' });
    return response.data;
  },
  getAccountStatus: async () => {
    const response = await api.get('/stripe-connect/account-status');
    return response.data;
  },
  createLoginLink: async () => {
    const response = await api.post('/stripe-connect/create-login-link', { source: 'web' });
    return response.data; // Returns { url: string, is_onboarding?: boolean }
  },
  getBalance: async () => {
    const response = await api.get('/stripe-connect/balance');
    return response.data;
  },
  withdraw: async (amount: number, instant?: boolean) => {
    const response = await api.post('/stripe-connect/withdraw', { amount, instant });
    return response.data;
  },
  getTransactions: async () => {
    const response = await api.get('/stripe-connect/transactions');
    return response.data;
  },
  getExternalAccounts: async () => {
    const response = await api.get('/stripe-connect/external-accounts');
    return response.data;
  },
  setupExternalAccount: async (type: 'card' | 'bank_account') => {
    const response = await api.post('/stripe-connect/setup-external-account', { type });
    return response.data;
  },
  createSetupIntent: async () => {
    const response = await api.post('/stripe-connect/create-setup-intent');
    return response.data;
  },
  attachPaymentMethod: async (payment_method_id: string, setup_intent_id?: string) => {
    const response = await api.post('/stripe-connect/attach-payment-method', { 
      payment_method_id, 
      setup_intent_id 
    });
    return response.data;
  },
  createPaymentMethod: async (payment_method_id: string) => {
    const response = await api.post('/stripe-connect/create-payment-method', { 
      payment_method_id 
    });
    return response.data;
  },
  confirmSetupIntent: async (setup_intent_id: string, payment_method_data?: any) => {
    const response = await api.post('/stripe-connect/confirm-setup-intent', { 
      setup_intent_id,
      payment_method_data
    });
    return response.data;
  },
  createExternalAccountFromToken: async (card_token: string) => {
    const response = await api.post('/stripe-connect/create-external-account-from-token', { 
      card_token 
    });
    return response.data;
  },
};

// Tax Information API
export const taxInfoAPI = {
  get: async () => {
    const response = await api.get('/tax-info');
    return response.data;
  },
  update: async (taxInfo: {
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    country?: string;
  }) => {
    const response = await api.put('/tax-info', taxInfo);
    return response.data;
  },
};

// Shipping Addresses API
export const shippingAddressesAPI = {
  getAll: async () => {
    const response = await api.get('/shipping-addresses');
    return response.data;
  },
  create: async (address: {
    address?: string;
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    instructions?: string;
    phone_number?: string;
    address_type?: string;
    apt_suite?: string;
    entry_code?: string;
    building_name?: string;
    is_default?: boolean;
  }) => {
    const response = await api.post('/shipping-addresses', address);
    return response.data;
  },
  update: async (id: number, address: {
    address?: string;
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    instructions?: string;
    phone_number?: string;
    address_type?: string;
    apt_suite?: string;
    entry_code?: string;
    building_name?: string;
    is_default?: boolean;
  }) => {
    const response = await api.put(`/shipping-addresses/${id}`, address);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/shipping-addresses/${id}`);
    return response.data;
  },
};

export const messagesAPI = {
  send: async (receiverId: number, message: string, listingId?: number) => {
    const response = await api.post('/messages', {
      receiver_id: receiverId,
      listing_id: listingId,
      message,
    });
    return response.data;
  },
  getConversations: async (opts?: { limit?: number; offset?: number }) => {
    const params: Record<string, number> = {};
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    const response = await api.get('/messages/conversations', { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },
  getMessages: async (otherUserId: number, listingId?: number) => {
    const params: any = {};
    if (listingId) params.listing_id = listingId;
    const response = await api.get(`/messages/${otherUserId}`, { params });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/messages/unread/count');
    return response.data;
  },
  deleteConversation: async (otherUserId: number) => {
    const response = await api.delete(`/messages/conversations/${otherUserId}`);
    return response.data;
  },
  blockUser: async (userId: number) => {
    const response = await api.post(`/messages/block/${userId}`);
    return response.data;
  },
  unblockUser: async (userId: number) => {
    const response = await api.delete(`/messages/block/${userId}`);
    return response.data;
  },
  getBlockStatus: async (userId: number) => {
    const response = await api.get(`/messages/block/status/${userId}`);
    return response.data;
  },
  reportUser: async (payload: {
    reported_user_id: number;
    reason: string;
    details?: string;
    message_id?: number;
    listing_id?: number;
  }) => {
    const response = await api.post('/messages/report', payload);
    return response.data;
  },
  getReportReasons: async () => {
    const response = await api.get('/messages/report-reasons');
    return response.data;
  },
};

// Offers API
export interface Offer {
  id: number;
  listing_id: number;
  buyer_id: number;
  seller_id: number;
  offer_amount: number;
  message?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  created_at: string;
  updated_at: string;
  buyer_name?: string;
  seller_name?: string;
  listing_title?: string;
  listing_price?: number;
  listing_image?: string;
}

export const offersAPI = {
  create: async (listingId: number, offerAmount: number, message?: string, deliveryAddress?: string) => {
    const response = await api.post('/offers', {
      listing_id: listingId,
      offer_amount: offerAmount,
      message,
      delivery_address: deliveryAddress,
    });
    return response.data;
  },
  getByListing: async (listingId: number) => {
    const response = await api.get(`/offers/listing/${listingId}`);
    return response.data;
  },
  getBySeller: async () => {
    const response = await api.get('/offers/seller');
    return response.data;
  },
  getByBuyer: async () => {
    const response = await api.get('/offers/buyer');
    return response.data;
  },
  accept: async (offerId: number, deliveryAddress?: string) => {
    const response = await api.post(`/offers/${offerId}/accept`, deliveryAddress ? { deliveryAddress } : {});
    return response.data;
  },
  reject: async (offerId: number) => {
    const response = await api.post(`/offers/${offerId}/reject`);
    return response.data;
  },
  cancel: async (offerId: number) => {
    const response = await api.post(`/offers/${offerId}/cancel`);
    return response.data;
  },
};

// Follows API
export const followsAPI = {
  followUser: async (userId: number) => {
    const response = await api.post(`/follows/users/${userId}`);
    return response.data;
  },
  unfollowUser: async (userId: number) => {
    const response = await api.delete(`/follows/users/${userId}`);
    return response.data;
  },
  followStore: async (storeId: number) => {
    const response = await api.post(`/follows/stores/${storeId}`);
    return response.data;
  },
  unfollowStore: async (storeId: number) => {
    const response = await api.delete(`/follows/stores/${storeId}`);
    return response.data;
  },
  checkUserFollowStatus: async (userId: number) => {
    const response = await api.get(`/follows/users/${userId}/status`);
    return response.data;
  },
  checkStoreFollowStatus: async (storeId: number) => {
    const response = await api.get(`/follows/stores/${storeId}/status`);
    return response.data;
  },
  getFollowingUsers: async () => {
    const response = await api.get('/follows/users/following');
    return response.data;
  },
  getFollowingStores: async () => {
    const response = await api.get('/follows/stores/following');
    return response.data;
  },
  getUserFollowerCount: async (userId: number) => {
    const response = await api.get(`/follows/users/${userId}/followers/count`);
    return response.data;
  },
  getUserFollowingCount: async (userId: number) => {
    const response = await api.get(`/follows/users/${userId}/following/count`);
    return response.data;
  },
  getStoreFollowerCount: async (storeId: number) => {
    const response = await api.get(`/follows/stores/${storeId}/followers/count`);
    return response.data;
  },
};

// Delivery Fee API
export const deliveryFeeAPI = {
  /** Get seller coordinates; pass userLat/userLon to get server-computed distance_miles (same as app). */
  getListingLocation: async (listingId: number, userLat?: number, userLon?: number) => {
    const params: Record<string, string> = {};
    if (userLat != null && Number.isFinite(userLat)) params.lat = String(userLat);
    if (userLon != null && Number.isFinite(userLon)) params.lon = String(userLon);
    const response = await api.get(`/delivery-fee/listing-location/${listingId}`, { params });
    return response.data;
  },
  getListingLocationsBatch: async (opts: {
    listing_ids: number[];
    lat?: number;
    lng?: number;
  }): Promise<Record<number, { latitude: number | null; longitude: number | null; distance_miles: number | null }>> => {
    const body: Record<string, unknown> = { listing_ids: opts.listing_ids };
    if (opts.lat != null && Number.isFinite(opts.lat)) body.lat = opts.lat;
    if (opts.lng != null && Number.isFinite(opts.lng)) body.lng = opts.lng;
    const response = await api.post('/delivery-fee/listing-locations-batch', body);
    const raw = response.data?.locations || {};
    const out: Record<number, { latitude: number | null; longitude: number | null; distance_miles: number | null }> = {};
    for (const [key, val] of Object.entries(raw)) {
      const id = parseInt(key, 10);
      if (!id) continue;
      const row = val as { latitude?: number | null; longitude?: number | null; distance_miles?: number | null };
      out[id] = {
        latitude: row.latitude ?? null,
        longitude: row.longitude ?? null,
        distance_miles: row.distance_miles ?? null,
      };
    }
    return out;
  },
  calculate: async (listingId: number, deliveryAddress: string, orderTotal?: number) => {
    const response = await api.post('/delivery-fee/calculate', {
      listing_id: listingId,
      delivery_address: deliveryAddress,
      order_total: orderTotal || 0,
    });
    return response.data;
  },
};

export default api;

// Force rebuild Thu Dec  4 19:08:55 PST 2025
// Build timestamp: 1764904798
